import { BookingDTOTransportMode } from '@forto-dev/booking-v2-api'
import { PointJSON } from 'features/BookingTemplates'
import { formatAddress, isAddress, isAirPort, isSeaPort, Port } from 'features/Directory'
import { has } from 'lodash-es'
import { FC } from 'react'
import { useI18n } from 'utils/localisation'

import { TTransportModeWithShip } from '../QuoteSheet/QuoteOption/QuoteTransportPlan'
import { IQuoteOption } from '../QuoteSheet/QuoteSheetOption'
import { LCLMainRate } from '../QuoteSheet/Rate/LCLRate'
import { BookingTimelineItem } from './BookingTimelineItem'

type BookingLCLScheduleProps = {
  option: IQuoteOption
  transportMode: BookingDTOTransportMode
}

const isOptionWithViaPortId = (option: IQuoteOption) => {
  return has(option.routeQuotes.mainQuote.rates, '0.via_ids')
}

const isOptionWithPOD = (option: IQuoteOption) => {
  const { destination } = option.routeQuotes.mainQuote.route.journey
  return isSeaPort(destination) || isAirPort(destination)
}

const StationsIconsMap: { [key: string]: TTransportModeWithShip } = {
  sea: 'ship',
  air: 'air',
  seaAir: 'air',
  rail: 'rail',
}

const formatPortOrAddress = (point: PointJSON): string => {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  return isAddress(point) ? formatAddress(point) : `${point._id} - ${(point as Port).name}`
}
const getOptionPOD = (option: IQuoteOption): Port | null => {
  return isOptionWithPOD(option) ? (option.routeQuotes.mainQuote.route.journey.destination as Port) : null
}

export const BookingLCLSchedule: FC<BookingLCLScheduleProps> = ({ option, transportMode }) => {
  const { origin, destination } = option.routeQuotes.mainQuote.route.journey
  const { translate } = useI18n()

  const originLabel =
    transportMode === 'air' && !isAddress(origin)
      ? 'quote_sheet-option-transport_plan-departure_airport'
      : 'quote_sheet-option-transport_plan-origin'
  const mainRate = option.routeQuotes.mainQuote.rates?.[0] as LCLMainRate

  // Origin can be null when creating manual quotes
  const originName = origin ? formatPortOrAddress(origin) : ''

  const pod = getOptionPOD(option)

  return (
    <>
      <BookingTimelineItem
        isFirstLeg
        timelineContent={{
          description: {
            label: translate(originLabel),
            value: originName,
          },
        }}
      />
      {isOptionWithViaPortId(option) && (
        <BookingTimelineItem
          icon="ship"
          timelineContent={{
            description: {
              label: translate('quote_sheet-option-transport_plan-sea_air_via'),
              value: mainRate?.via_ids ? mainRate.via_ids.join(' ') : '',
            },
          }}
        />
      )}
      {isOptionWithPOD(option) && (
        <BookingTimelineItem
          icon={StationsIconsMap[transportMode]}
          timelineContent={{
            description: {
              label: translate(
                transportMode === 'air'
                  ? 'quote_sheet-option-transport_plan-arrival_airport'
                  : 'quote_sheet-option-transport_plan-POD'
              ),
              value: pod ? `${pod._id} - ${pod.name}` : '-',
            },
          }}
        />
      )}
      <BookingTimelineItem
        isLastLeg
        timelineContent={{
          description: {
            label: translate('quote_sheet-option-transport_plan-destination'),
            value: isAddress(destination) ? destination.city : '-',
          },
        }}
      />
    </>
  )
}
